import { JSX } from 'react';

import { ComponentDynamicSvgArgs } from '../../components/types/component-dynamic-svg.type';

// Kegerator tap mounting pattern (top-of-countertop). Geometry is in inches,
// centered at (0, 0): a 2"D center tap hole and four 0.25"D mounting screw
// holes on a 2.5" x 2.75" rectangular pattern. Labels follow the caller's
// `fontSize` and are placed radially around the geometry matching the source
// spec: c-to-c "front and back" sits ABOVE, c-to-c "both sides" sits to the
// RIGHT, and each side stacks its labels with row spacing proportional to
// the font so they never collide. Leader-line outer endpoints follow each
// label's row; inner endpoints (the arrowheads) are pinned to real features.

const CENTER_R = 1;          // 2"D center hole
const MOUNT_R = 0.125;       // 0.25"D mounting holes
const MOUNT_X = 1.25;        // 2.5" c-to-c on x
const MOUNT_Y = 1.375;       // 2.75" c-to-c on y
const PLATE_W = 2.5;
const PLATE_H = 2.75;
const STROKE = 0.0625;

const BASE_LABEL_FONT = 0.4;      // fallback when caller passes no fontSize
const SMALL_FONT_RATIO = 0.8;     // 0.32 / 0.4 — preserves source two-tier sizing
const LINE_HEIGHT_FACTOR = 1.4;   // multiplier on labelFont for row spacing
const FIRST_ROW_GAP = 0.5;        // extra gap (in line-heights) between geometry and first row

const ARROW_MARKER_ID = 'c124kegtap-arrow';

export const c124kegtap = {
	// Six callouts stack illegibly at real scale (GitHub #282/#385) -- render them in an off-panel detail block instead of in place.
	offPanelDetail: true,

	item: ({ x, y, opacity = 1, fontSize }: ComponentDynamicSvgArgs): JSX.Element => {
		const tx = x ?? 0;
		const ty = y ?? 0;

		const labelFont = fontSize ?? BASE_LABEL_FONT;
		const smallFont = labelFont * SMALL_FONT_RATIO;
		const lineHeight = labelFont * LINE_HEIGHT_FACTOR;

		// Row Y positions. Above rows climb upward; below rows descend downward.
		const aboveY = (row: number): number => -PLATE_H / 2 - (FIRST_ROW_GAP + row - 1) * lineHeight;
		const belowY = (row: number): number => PLATE_H / 2 + (FIRST_ROW_GAP + row - 1) * lineHeight + labelFont;

		// ABOVE (2 stacked rows): "2.5" front/back" hugs the top edge; "2"D
		// center hole" lives one row farther up, with a leader down to the hole.
		const yCToCFrontBack = aboveY(1);
		const yCenterHole    = aboveY(2);

		// RIGHT (2 stacked rows, vertically centered around y=0):
		const yCToCBothSides = -lineHeight / 2;
		const yRightToCenter =  lineHeight / 2;

		// BELOW (2 stacked rows): "1.375" to center" closest to the rect (its
		// leader points up to the down c-to-c reference); "0.25"D outer holes"
		// one row farther, leader up to the bottom-right mount.
		const yLeftToCenter = belowY(1);
		const yOuterHoles   = belowY(2);

		// X positions for the right-side column — start-anchored just past the
		// rect's right edge so the labels visually sit on the right.
		const xRightCol = PLATE_W / 2 + labelFont * 0.3;

		return <g transform={`translate(${tx}, ${ty})`} opacity={opacity} pointerEvents="none">
			<g fill="none" stroke="black" strokeWidth={STROKE}>
				<defs>
					<marker
						id={ARROW_MARKER_ID}
						markerWidth={0.25}
						markerHeight={0.25}
						refX={0.23}
						refY={0.125}
						orient="auto-start-reverse"
						markerUnits="userSpaceOnUse"
					>
						<path d="M0,0.02 L0.25,0.125 L0,0.23 Z" fill="black" stroke="none"/>
					</marker>
				</defs>

				<circle cx={0} cy={0} r={CENTER_R}/>
				<circle cx={-MOUNT_X} cy={-MOUNT_Y} r={MOUNT_R}/>
				<circle cx={MOUNT_X} cy={-MOUNT_Y} r={MOUNT_R}/>
				<circle cx={-MOUNT_X} cy={MOUNT_Y} r={MOUNT_R}/>
				<circle cx={MOUNT_X} cy={MOUNT_Y} r={MOUNT_R}/>

				<rect x={-PLATE_W / 2} y={-PLATE_H / 2} width={PLATE_W} height={PLATE_H}/>

				<line x1={0} y1={0} x2={MOUNT_X} y2={0}/>
				<line x1={0} y1={0} x2={0} y2={MOUNT_Y}/>

				{/* ABOVE row 1: c-to-c front/back — start-anchored at left edge of rect */}
				<text x={-PLATE_W / 2} y={yCToCFrontBack} fontSize={smallFont} textAnchor="start" fill="black" stroke="none">
					2.5&quot; center to center on front and back
				</text>

				{/* ABOVE row 2: 2"D center hole — leader from label down to center hole */}
				<line x1={-2.6} y1={yCenterHole + labelFont * 0.3} x2={-0.93} y2={-0.37} markerEnd={`url(#${ARROW_MARKER_ID})`}/>
				<text x={-2.6} y={yCenterHole} fontSize={labelFont} textAnchor="end" fill="black" stroke="none">
					2&quot;D of center hole
				</text>

				{/* RIGHT upper: c-to-c both sides — leader from label left to right edge of rect */}
				<line x1={xRightCol} y1={yCToCBothSides + labelFont * 0.3} x2={MOUNT_X} y2={0} markerEnd={`url(#${ARROW_MARKER_ID})`}/>
				<text x={xRightCol} y={yCToCBothSides + labelFont} fontSize={smallFont} textAnchor="start" fill="black" stroke="none">
					2.75&quot; center to center on both sides
				</text>

				{/* RIGHT lower: 1.25" to center — leader from label left to right c-to-c reference */}
				<line x1={xRightCol} y1={yRightToCenter + labelFont * 0.3} x2={1.1} y2={0.03} markerEnd={`url(#${ARROW_MARKER_ID})`}/>
				<text x={xRightCol} y={yRightToCenter + labelFont} fontSize={labelFont} textAnchor="start" fill="black" stroke="none">
					1.25&quot; to center
				</text>

				{/* BELOW row 1: 1.375" to center — leader from label up to down c-to-c reference */}
				<line x1={-2.4} y1={yLeftToCenter - labelFont * 0.9} x2={-0.05} y2={1.2} markerEnd={`url(#${ARROW_MARKER_ID})`}/>
				<text x={-2.5} y={yLeftToCenter} fontSize={labelFont} textAnchor="end" fill="black" stroke="none">
					1.375&quot; to center
				</text>

				{/* BELOW row 2: 0.25"D outer holes — leader from label up to bottom-right mount */}
				<line x1={1.95} y1={yOuterHoles - labelFont * 0.9} x2={1.34} y2={MOUNT_Y + MOUNT_R} markerEnd={`url(#${ARROW_MARKER_ID})`}/>
				<text x={1.4} y={yOuterHoles} fontSize={labelFont} textAnchor="start" fill="black" stroke="none">
					0.25&quot;D of 4x outer holes
				</text>
			</g>
		</g>;
	},

	// Material actually removed from the countertop: the five holes filled with
	// the caller-supplied colour (black for masks, background colour for the
	// visible cutout overlay).
	cutout: ({ x, y, opacity = 1, fill = '#EDF2F7' }: ComponentDynamicSvgArgs): JSX.Element => {
		const tx = x ?? 0;
		const ty = y ?? 0;
		return <g transform={`translate(${tx}, ${ty})`} opacity={opacity} pointerEvents="none">
			<circle cx={0} cy={0} r={CENTER_R} fill={fill} stroke="none"/>
			<circle cx={-MOUNT_X} cy={-MOUNT_Y} r={MOUNT_R} fill={fill} stroke="none"/>
			<circle cx={MOUNT_X} cy={-MOUNT_Y} r={MOUNT_R} fill={fill} stroke="none"/>
			<circle cx={-MOUNT_X} cy={MOUNT_Y} r={MOUNT_R} fill={fill} stroke="none"/>
			<circle cx={MOUNT_X} cy={MOUNT_Y} r={MOUNT_R} fill={fill} stroke="none"/>
		</g>;
	}
};
